<template>
  <div class="mt-1 mb-4 flex w-full">
    <DTabs v-model:value="tab" class="!w-full">
      <DTabList>
        <DTab value="0">My Library</DTab>
        <DTab value="1">Shared Library</DTab>
      </DTabList>
      <DTabPanels class="p-0">
        <DTabPanel value="0">
          <div class="my-2 flex w-full flex-col gap-4" v-if="showNew && params.filter === 'led'">
            <div class="flex flex-col gap-2">
              <FormInputText label="Name" v-model="newLibrary.name" />

              <div class="grid grid-cols-7 items-center text-gray-400">
                <div class="col-span-2 mt-2 mb-2 flex text-gray-400">Brand</div>
                <div class="col-span-5 flex flex-col">
                  <div class="flex gap-2">
                    <DSelect
                      v-model="newLibrary.brand"
                      :options="brandStore.brands"
                      optionLabel="name"
                      optionValue="code"
                      class="flex-1" />
                  </div>
                </div>
              </div>
            </div>
            <DDivider />
            <div class="mt-2 flex items-center gap-2">
              <div class="w-full">
                <FormInputText label="Resolution Width" v-model="newLibrary.resolution_width" />
              </div>
              <div class="w-full">
                <FormInputText label="Resolution Height" v-model="newLibrary.resolution_height" />
              </div>
            </div>
            <DDivider />
            <div class="mt-2 flex items-center gap-2">
              <div class="flex w-full items-center gap-2">
                <div class="flex text-gray-400">Tile Width</div>
                <DInputNumber
                  label="Width"
                  v-model="panelWidthMMValue"
                  fluid
                  :step="0.01"
                  :maxFractionDigits="2"
                  :suffix="lengthUnitLabel"
                  buttonLayout="stacked" />
              </div>
              <div class="flex w-full items-center gap-2">
                <div class="flex text-gray-400">Tile Height</div>
                <DInputNumber
                  label="Height"
                  v-model="panelHeightMMValue"
                  fluid
                  :step="0.01"
                  :maxFractionDigits="2"
                  :suffix="lengthUnitLabel"
                  buttonLayout="stacked" />
              </div>
              <div class="flex w-full items-center gap-2">
                <div class="flex text-gray-400">Brightness (nits)</div>
                <DInputNumber
                  label="Brightness"
                  v-model="newLibrary.brightness_nits"
                  fluid
                  :step="1"
                  :maxFractionDigits="0"
                  buttonLayout="stacked" />
              </div>
            </div>
          </div>

          <div class="mt-4 flex flex-col" v-if="showNew && params.filter !== 'led'">
            <div class="flex flex-col gap-2">
              <FormInputText label="Name" v-model="newLibrary.name" />

              <div class="grid grid-cols-7 items-center text-gray-400">
                <div class="col-span-2 mt-2 mb-2 flex text-gray-400">Brand</div>
                <div class="col-span-5 flex flex-col">
                  <div class="flex gap-2">
                    <DSelect
                      v-model="newLibrary.brand"
                      :options="brandStore.brands"
                      optionLabel="name"
                      optionValue="code"
                      class="flex-1" />
                  </div>
                </div>
              </div>
            </div>
            <DDivider />
            <div class="flex items-center gap-2">
              <div class="w-full">
                <FormInputText label="Lumens" v-model="newLibrary.lumens" />
              </div>
              <div class="w-full">
                <FormInputText label="Throw Ratio" v-model="newLibrary.throw_ratio" />
              </div>
            </div>

            <div class="mt-4 flex gap-2">
              <div class="w-full">
                <FormInputText label=" Width" v-model="newLibrary.resolution_width" />
              </div>
              <div class="w-full">
                <FormInputText label=" Height" v-model="newLibrary.resolution_height" />
              </div>
            </div>
            <DDivider />
            <div class="flex flex-col">
              <div class="flex space-x-6">
                <div class="col-span-7 mt-2 flex text-gray-400">3D Mesh (optional)</div>
                <div
                  v-if="newLibrary.mesh && !meshFile"
                  class="flex items-center gap-2 rounded bg-gray-800 p-2 text-sm text-green-400">
                  <i class="pi pi-check-circle"></i>
                  <span>Current mesh: {{ newLibrary.mesh.split('/').pop() }}</span>
                </div>
                <div class="flex flex-col gap-2">
                  <div class="hover:border-primary w-full rounded-lg border border-gray-700 bg-gray-950">
                    <input
                      type="file"
                      @change="handleMeshFileSelect"
                      ref="meshFileInput"
                      :disabled="meshValidating"
                      class="block w-full p-0.5 text-sm text-gray-400 file:mr-4 file:rounded file:bg-gray-950 file:px-4 file:py-2 file:text-sm file:text-white" />
                  </div>
                  <div>
                    <div v-if="meshValidating" class="text-sm text-blue-400">Validating mesh file...</div>
                    <div v-if="meshValidationError" class="text-sm text-red-400">{{ meshValidationError }}</div>
                    <div v-if="meshValidationSuccess" class="text-sm text-green-400">{{ meshValidationSuccess }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <template v-if="!showNew">
            <LedDataTable
              v-if="params.filter === 'led'"
              :data="tableData"
              :mode="params.filter"
              :showEdit="true"
              @select="selectProduct"
              @edit="editLibrary" />
            <ProjectorDataTable
              v-if="params.filter === 'projector'"
              :data="tableData"
              :mode="params.filter"
              :showEdit="true"
              @select="selectProduct"
              @edit="editLibrary" />
          </template>
        </DTabPanel>
        <DTabPanel value="1">
          <div class="grid w-full grid-cols-7">
            <div class="col-span-7">
              <template v-if="!showNew">
                <LedDataTable
                  v-if="params.filter === 'led'"
                  :data="tableData"
                  :mode="params.filter"
                  @select="selectProduct" />
                <ProjectorDataTable
                  v-if="params.filter === 'projector'"
                  :data="tableData"
                  :mode="params.filter"
                  @select="selectProduct" />
              </template>
            </div>
          </div>
        </DTabPanel>
      </DTabPanels>
    </DTabs>
  </div>

  <div class="my-2 flex items-center justify-end gap-2">
    <DButton
      type="button"
      :label="tab === '0' && showNew ? 'Back' : 'Cancel'"
      :icon="tab === '0' && showNew ? 'pi pi-arrow-left' : 'pi pi-times'"
      class="border border-gray-600"
      @click="
        () => {
          showNew ? (showNew = false) : closeDialog({ buttonType: 'Cancel' });
        }
      "></DButton>
    <DButton
      v-if="showNew"
      :autofocus="showNew ? true : false"
      type="button"
      label="Save"
      icon="pi pi-save"
      severity="primary"
      @click.prevent="saveNew"
      :loading="loading"
      :disabled="loading"></DButton>

    <DButton
      v-if="!showNew && tab === '0'"
      :autofocus="!showNew ? true : false"
      autofocus
      type="button"
      label="Create New"
      severity="primary"
      icon="pi pi-plus"
      @click="
        () => {
          tab = '0';
          showNewProjectorForm();
        }
      "></DButton>
  </div>
</template>

<script setup>
import { FormInputText } from '@/components';
import { DInputNumber, DSelect, useToast } from '@disguise/cloud-ui';
import { computed, inject, onMounted, ref, watch } from 'vue';

import { DButton, DDivider, DTab, DTabList, DTabPanel, DTabPanels, DTabs } from '@disguise/cloud-ui';

import { useDataValidation } from '@/composables/useDataValidation';
import { useUnitConversion } from '@/composables/useUnitConversion';
import { useBrandStore } from '@/stores/api/brand';
import { useLedPanelStore } from '@/stores/api/ledpanel';
import { useProjectorStore } from '@/stores/api/projector';
import { zipMeshFile } from '@/utils/fileUtils';
import LedDataTable from './partials/LedDataTable.vue';
import ProjectorDataTable from './partials/ProjectorDataTable.vue';

const brandStore = useBrandStore();
const toast = useToast();
const { convertLength, toMeters, getLengthUnitLabel } = useUnitConversion();
const { validateProjectorMesh } = useDataValidation();

const lengthUnitLabel = computed(() => getLengthUnitLabel());
const projectorStore = useProjectorStore();
const ledPanelStore = useLedPanelStore();
const dialogRef = inject('dialogRef');
const showNew = ref(false);
const params = ref({});
const tab = ref('0');
const meshFile = ref(null);
const meshValidating = ref(false);
const meshValidationError = ref('');
const meshValidationSuccess = ref('');
const meshFileInput = ref(null);

const newLibrary = ref({});

const newBrandName = ref('');
const loading = ref(false);

onMounted(() => {
  params.value = dialogRef.value.data;
  loadLibrary();
});

const tableData = computed(() => {
  let data = [];
  if (params.value?.filter === 'led') {
    data = tab.value === '0' ? ledPanelStore.personal : ledPanelStore.global;
  }
  if (params.value?.filter === 'projector') {
    data = tab.value === '0' ? projectorStore.personal : projectorStore.global;
  }
  if (tab.value === '0') return data;
  return data.filter((item) => item.visibility === 'disguise');
});

const loadLibrary = async () => {
  brandStore.fetch();

  if (params.value?.filter === 'led') {
    await ledPanelStore.fetchSelf();
    await ledPanelStore.fetchGlobal();

    if (ledPanelStore.personal.length === 0) {
      updateTab('1');
    }
  } else {
    await projectorStore.fetchSelf();
    await projectorStore.fetchGlobal();

    if (projectorStore.personal.length === 0) {
      updateTab('1');
    }
  }
};

const updateTab = (newTab) => {
  tab.value = newTab;
};

const closeDialog = (e) => {
  dialogRef.value.close(e);
};

const selectProduct = (data) => {
  dialogRef.value.close(data);
};

const showNewProjectorForm = () => {
  resetMeshState();
  if (params.value?.filter === 'led') {
    newLibrary.value = {
      name: 'New LED Panel',
      brand: '',
      brightness_nits: 100,
      resolution_height: 128,
      resolution_width: 128,
      panel_width_mm: 500,
      panel_height_mm: 500,
    };

    showNew.value = true;
    return;
  } else {
    newLibrary.value = {
      name: 'New Projector',
      brand: '',
      lumens: 10000,
      throw_ratio: 2.0,
      resolution_height: 1080,
      resolution_width: 1920,
    };
  }
  showNew.value = true;
};

const panelWidthMMValue = computed({
  get() {
    return convertLength(newLibrary.value.panel_width_mm / 1000);
  },
  set(newValue) {
    newLibrary.value.panel_width_mm = toMeters(newValue) * 1000;
  },
});

const panelHeightMMValue = computed({
  get() {
    return convertLength(newLibrary.value.panel_height_mm / 1000);
  },
  set(newValue) {
    newLibrary.value.panel_height_mm = toMeters(newValue) * 1000;
  },
});

const saveNew = async () => {
  if (!newLibrary.value.name.trim()) {
    toast.add({
      severity: 'error',
      summary: 'Validation Error',
      detail: 'Name is required',
      life: 3000,
      group: 'br',
    });
    return;
  }
  loading.value = true;
  try {
    let response;
    if (params.value?.filter === 'led') {
      if (newLibrary.value.code) {
        response = await ledPanelStore.update(newLibrary.value.code, {
          ...newLibrary.value,
        });

        await ledPanelStore.fetchSelf();
        showNew.value = false;
        tab.value = '0';
      } else {
        response = await ledPanelStore.store({ ...newLibrary.value });
      }
      const { error } = response;
      if (error) {
        handleApiResponse(error);
        return;
      }

      // Refresh LED panel list
      if (response) {
        await ledPanelStore.fetchSelf();
        toast.add({
          severity: 'success',
          summary: 'Success',
          detail: 'LED panel created successfully',
          life: 3000,
          group: 'tr',
        });
      }
    } else if (params.value?.filter === 'projector') {
      if (newLibrary.value.code) {
        // Editing existing projector
        const { code, ...updateData } = newLibrary.value;
        if (meshFile.value) {
          const zippedFile = await zipMeshFile(meshFile.value);
          const formData = new FormData();
          Object.entries(updateData).forEach(([key, value]) => {
            if (value !== null && value !== undefined) {
              formData.append(key, typeof value === 'object' ? JSON.stringify(value) : value);
            }
          });
          formData.append('mesh', zippedFile);
          await projectorStore.update(code, formData);
        } else {
          const { mesh, ...dataWithoutMesh } = newLibrary.value;
          await projectorStore.update(code, dataWithoutMesh);
        }
        await projectorStore.fetchSelf();

        showNew.value = false;
        tab.value = '0';
        resetMeshState();
      } else {
        // Creating new projector
        const payload = {
          name: newLibrary.value.name,
          brand: newLibrary.value.brand,
          lumens: newLibrary.value.lumens,
          throw_ratio: newLibrary.value.throw_ratio,
          resolution_width: newLibrary.value.resolution_width,
          resolution_height: newLibrary.value.resolution_height,
        };

        if (meshFile.value) {
          const zippedFile = await zipMeshFile(meshFile.value);
          const formData = new FormData();
          Object.entries(payload).forEach(([key, value]) => {
            if (value !== null && value !== undefined) {
              formData.append(key, value);
            }
          });
          formData.append('mesh', zippedFile);
          response = await projectorStore.store(formData);
        } else {
          response = await projectorStore.store(payload);
        }
      }
      const { error } = response;
      if (error) {
        handleApiResponse(error);
        return;
      }

      if (response) {
        await projectorStore.fetchSelf();

        toast.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Projector created successfully',
          life: 3000,
          group: 'tr',
        });
      }
    } else {
      console.error('Invalid filter type');
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Invalid filter type',
        life: 3000,
        group: 'tr',
      });
      return;
    }
    if (response) {
      newLibrary.value = {};
      showNew.value = false;
      resetMeshState();
    }
  } catch (error) {
    console.error('Error saving library item:', error);
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to save item. Please try again.',
      life: 5000,
      group: 'tr',
    });
  } finally {
    loading.value = false;
  }
};

const editLibrary = (library) => {
  newLibrary.value = {
    ...library,
    brand: typeof library.brand === 'object' ? library.brand?.code : library.brand,
  };
  showNew.value = true;
};

const addNewBrand = async () => {
  if (!newBrandName.value.trim()) return;

  const response = await brandStore.store({ name: newBrandName.value });
  if (response) {
    await brandStore.fetch();
    newLibrary.value.brand = response.code;

    newBrandName.value = '';
  }
};

const resetMeshState = () => {
  meshFile.value = null;
  meshValidating.value = false;
  meshValidationError.value = '';
  meshValidationSuccess.value = '';
  if (meshFileInput.value) meshFileInput.value.value = '';
};

const handleMeshFileSelect = async (event) => {
  const file = event.target.files[0];
  if (!file) return;

  meshValidationError.value = '';
  meshValidationSuccess.value = '';
  meshValidating.value = true;

  try {
    await validateProjectorMesh(file);
    meshValidationSuccess.value = 'Valid mesh file selected';
    meshFile.value = file;
  } catch (err) {
    meshValidationError.value = `Invalid mesh file selected`;
    meshFile.value = null;
    event.target.value = '';
  } finally {
    meshValidating.value = false;
  }
};

watch(tab, (newVal) => {
  if (newVal === '1') {
    showNew.value = false;
  }
});
</script>
